<template>
  <div class="space-y-4">

    <!-- Edit controls — full width -->
    <div class="flex justify-end">
      <button v-if="!isEditing" @click="startEdit"
        class="text-sm px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50">
        Edit Consultation
      </button>
      <div v-else class="flex gap-2">
        <button @click="cancelEdit"
          class="text-sm px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button @click="save" :disabled="saving"
          class="text-sm px-3 py-1.5 rounded bg-custom-blue text-white hover:opacity-90 disabled:opacity-50">
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </div>

    <!-- Sections grid -->
    <div class="grid grid-cols-2 gap-6 items-start">

      <!-- Chief Complaint -->
      <section v-if="complaint">
        <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Presenting Complaints</h3>
        <div class="bg-gray-50 rounded-lg p-4 space-y-3">
          <!-- New multi-symptom format -->
          <div v-if="complaintSymptoms.length" class="space-y-1.5">
            <div v-for="(s, i) in complaintSymptoms" :key="i" class="flex items-center gap-2 text-sm">
              <span class="w-2 h-2 rounded-full bg-blue-400 inline-block shrink-0"></span>
              <span class="text-gray-800">{{ s.chief_complaint }}</span>
              <span v-if="s.duration" class="text-gray-500">— {{ s.duration }} {{ s.duration_type }}</span>
            </div>
          </div>
          <p v-else class="text-gray-400 text-sm">—</p>
          <div v-if="complaint.history_of_illness" class="pt-1 border-t border-gray-200">
            <p class="text-xs text-gray-500">History of Illness</p>
            <p class="text-gray-800 text-sm">{{ complaint.history_of_illness }}</p>
          </div>
        </div>
      </section>

      <!-- Doctor's Findings -->
      <section v-if="consultation">
        <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Doctor's Findings</h3>
        <div v-if="!isEditing" class="bg-gray-50 rounded-lg p-4 min-h-16">
          <p class="text-gray-800 whitespace-pre-line">{{ consultation.findings || '—' }}</p>
        </div>
        <textarea v-else v-model="form.findings" rows="5"
          class="w-full rounded border-gray-300 bg-gray-50 text-sm p-3" />
      </section>

      <!-- Diagnoses -->
      <section v-if="diagnoses.length">
        <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Diagnoses</h3>
        <div class="space-y-2">
          <div v-for="(item, index) in diagnoses" :key="index"
            class="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3">
            <span class="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded mt-0.5 flex-shrink-0">
              {{ item.diagnosis?.code || '—' }}
            </span>
            <div>
              <p class="text-gray-800 text-sm">{{ item.diagnosis?.name || '—' }}</p>
              <p class="text-xs text-gray-400">{{ item.diagnosis_type === 1 ? 'Primary' : 'Secondary' }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Physical Examination -->
      <section v-if="physicalExam">
        <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Physical Examination</h3>
        <div v-if="!isEditing" class="grid grid-cols-2 gap-3">
          <div v-for="(value, label) in physicalExamFields" :key="label">
            <p class="text-xs text-gray-500">{{ label }}</p>
            <p class="text-gray-800 text-sm">{{ value || '—' }}</p>
          </div>
        </div>
        <div v-else class="grid grid-cols-2 gap-3">
          <div v-for="field in physicalExamFormFields" :key="field.key">
            <label class="text-xs text-gray-500">{{ field.label }}</label>
            <input v-model="form.physicalExam[field.key]" type="text"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm" />
          </div>
        </div>
      </section>

    </div>

    <div v-if="!complaint && !consultation && !diagnoses.length && !physicalExam"
      class="text-sm text-gray-400 py-4">
      No consultation data recorded for this visit.
    </div>

  </div>
</template>

<script>
import apiClient from '@/services/api'

export default {
  name: 'ConsultationStageView',

  props: {
    detail: { type: Object, required: true },
  },

  emits: ['detail-updated'],

  data() {
    return {
      isEditing: false,
      saving:    false,
      form:      { findings: '', physicalExam: {} },
    }
  },

  computed: {
    complaint()    { return this.detail.complaint || null },
    complaintSymptoms() {
      const c = this.complaint
      if (!c) return []
      if (c.symptoms?.length) return c.symptoms
      if (c.chief_complaint) return [{ chief_complaint: c.chief_complaint, duration: c.duration, duration_type: c.duration_type }]
      return []
    },
    consultation() { return this.detail.consultation || null },
    diagnoses()    { return this.detail.diagnosis || [] },
    physicalExam() { return this.detail.physical_exam || null },

    physicalExamFields() {
      if (!this.physicalExam) return {}
      return {
        'General':                this.physicalExam.general,
        'Respiratory System':     this.physicalExam.respiratory_system,
        'Cardiovascular System':  this.physicalExam.cadiovascular_system,
        'Central Nervous System': this.physicalExam.cns,
        'Abdomen':                this.physicalExam.abdomen,
        'Genitourinary':          this.physicalExam.genitourinary,
        'Ear, Nose & Throat':     this.physicalExam.ent,
        'Skin':                   this.physicalExam.skin,
        'Musculoskeletal':        this.physicalExam.musculoskeletal,
        'Other':                  this.physicalExam.other,
      }
    },

    physicalExamFormFields() {
      return [
        { key: 'general',              label: 'General' },
        { key: 'respiratory_system',   label: 'Respiratory System' },
        { key: 'cadiovascular_system', label: 'Cardiovascular System' },
        { key: 'cns',                  label: 'Central Nervous System' },
        { key: 'abdomen',              label: 'Abdomen' },
        { key: 'genitourinary',        label: 'Genitourinary' },
        { key: 'ent',                  label: 'Ear, Nose & Throat' },
        { key: 'skin',                 label: 'Skin' },
        { key: 'musculoskeletal',      label: 'Musculoskeletal' },
        { key: 'other',                label: 'Other' },
      ]
    },
  },

  methods: {
    startEdit() {
      this.form = {
        findings: this.consultation?.findings || '',
        physicalExam: {
          general:              this.physicalExam?.general              || '',
          respiratory_system:   this.physicalExam?.respiratory_system   || '',
          cadiovascular_system: this.physicalExam?.cadiovascular_system || '',
          cns:                  this.physicalExam?.cns                  || '',
          abdomen:              this.physicalExam?.abdomen              || '',
          genitourinary:        this.physicalExam?.genitourinary        || '',
          ent:                  this.physicalExam?.ent                  || '',
          skin:                 this.physicalExam?.skin                 || '',
          musculoskeletal:      this.physicalExam?.musculoskeletal      || '',
          other:                this.physicalExam?.other                || '',
        },
      }
      this.isEditing = true
    },

    cancelEdit() {
      this.isEditing = false
    },

    async save() {
      this.saving = true
      try {
        const calls = []
        if (this.consultation?.id) {
          calls.push(apiClient.updateConsultation(this.consultation.id, { findings: this.form.findings }))
        }
        if (this.physicalExam?.id) {
          calls.push(apiClient.updatePhysicalExam(this.physicalExam.id, this.form.physicalExam))
        }

        const results = await Promise.all(calls)
        const failed = results.find(r => !r.success)
        if (failed) {
          this.$toast.error(failed.message || 'Failed to save')
          return
        }

        this.$emit('detail-updated')
        this.isEditing = false
        this.$toast.success('Consultation updated')
      } catch {
        this.$toast.error('Failed to save consultation')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
